<template>
  <div class="mt-4 space-y-4" id="contentPromotionOffersReport">
    <div class="rounded-lg border bg-white p-4">
      <div class="flex flex-wrap items-end gap-4">
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Fec. de inicio
          </span>
          <input
            v-model="filters.dateStart"
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            @keydown.enter.prevent
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Fec. término
          </span>
          <input
            v-model="filters.dateEnd"
            type="date"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            @keydown.enter.prevent
          />
        </label>
        <label class="block w-full md:w-auto">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Tipo de oferta
          </span>
          <select
            v-model="filters.promotionType"
            class="border-2 bg-gray-50 py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[180px] font-semibold w-full focus:border-blue-500 uppercase"
          >
            <option value="ALL">todas</option>
            <option value="PRECIO_ESPECIAL">precio especial</option>
            <option value="CANTIDAD_BONIFICADA">cantidad bonificada</option>
            <option value="PAQUETE">paquete</option>
          </select>
        </label>
        <label class="block flex-1 min-w-[240px]">
          <span class="block mb-1 text-xs font-medium text-gray-800">
            Buscar oferta, producto, cliente o comprobante
          </span>
          <input
            v-model="filters.search"
            type="text"
            class="py-2 px-2 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500 appearance-none"
            placeholder="Ej: 3x2, acetaminofen, cliente o número de venta"
            @keydown.enter.prevent="generateReport"
          />
        </label>
        <button
          class="bg-blue-500 text-white hover:bg-blue-600 border-2 border-blue-500 py-2 rounded-md active:scale-95 px-3 text-sm w-full md:w-auto"
          @click="generateReport"
        >
          <font-awesome-icon icon="fa-solid fa-search" class="rotate-90 mr-1" />
          Buscar
        </button>
        <button
          class="bg-slate-100 text-slate-700 hover:bg-slate-200 border-2 border-slate-200 py-2 rounded-md active:scale-95 px-3 text-sm w-full md:w-auto"
          @click="clearFilters"
        >
          Limpiar filtros
        </button>
      </div>
      <div class="grid gap-4 mt-4 lg:grid-cols-2">
        <div class="bg-blue-50 px-3 py-3 rounded-md border-2 border-blue-100">
          <SearchProducts @send-data="getDataProduct" />
          <div
            v-if="filters.productName"
            class="mt-2 text-sm text-blue-700 font-semibold flex flex-wrap items-center gap-2"
          >
            <span>Producto:</span>
            <span class="bg-white border border-blue-200 rounded-full px-3 py-1">
              {{ filters.productName }}
            </span>
            <button class="text-xs text-red-500" @click="removeProductFilter">
              Quitar
            </button>
          </div>
        </div>
        <div class="bg-blue-50 px-3 py-3 rounded-md border-2 border-blue-100">
          <SearchCustomer @send-data="getDataCustomer" />
          <div
            v-if="filters.customerName"
            class="mt-2 text-sm text-blue-700 font-semibold flex flex-wrap items-center gap-2"
          >
            <span>Cliente:</span>
            <span class="bg-white border border-blue-200 rounded-full px-3 py-1">
              {{ filters.customerName }}
            </span>
            <button class="text-xs text-red-500" @click="removeCustomerFilter">
              Quitar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <article
        v-for="card in summaryCards"
        :key="card.title"
        class="rounded-xl border bg-white p-4 shadow-sm"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {{ card.title }}
        </p>
        <h3 class="mt-2 text-2xl font-bold text-slate-700">{{ card.value }}</h3>
        <p class="mt-1 text-sm text-slate-500">{{ card.description }}</p>
      </article>
    </div>

    <div class="grid gap-4 xl:grid-cols-3">
      <section class="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div class="border-b px-4 py-3">
          <h3 class="text-sm font-bold text-slate-700">Top ofertas ejecutadas</h3>
          <p class="text-xs text-slate-500">Qué promoción más se está aplicando.</p>
        </div>
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <thead class="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th class="text-left px-4 py-3">Oferta</th>
                <th class="text-right px-4 py-3">Veces</th>
                <th class="text-right px-4 py-3">Gratis</th>
              </tr>
            </thead>
            <tbody class="text-sm text-slate-600">
              <tr v-for="item in rankings.promotions" :key="item.idpromocion + item.nombre_promocion" class="odd:bg-white even:bg-slate-50">
                <td class="px-4 py-3 min-w-[220px]">
                  <p class="font-semibold text-slate-700">{{ item.nombre_promocion }}</p>
                  <p class="text-xs text-slate-500">{{ item.tipo_promocion_label }}</p>
                </td>
                <td class="px-4 py-3 text-right">{{ format(item.executions) }}</td>
                <td class="px-4 py-3 text-right">{{ format(item.bonus_quantity) }}</td>
              </tr>
              <tr v-if="!rankings.promotions.length && !loading">
                <td colspan="3" class="px-4 py-6 text-center text-slate-400">
                  No hay ofertas aplicadas en este filtro.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div class="border-b px-4 py-3">
          <h3 class="text-sm font-bold text-slate-700">Rendimiento por tipo</h3>
          <p class="text-xs text-slate-500">Sirve para decidir qué formato de oferta impulsar.</p>
        </div>
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <thead class="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th class="text-left px-4 py-3">Tipo</th>
                <th class="text-right px-4 py-3">Ganancia</th>
                <th class="text-right px-4 py-3">Margen</th>
              </tr>
            </thead>
            <tbody class="text-sm text-slate-600">
              <tr v-for="item in rankings.promotion_types" :key="item.tipo_promocion" class="odd:bg-white even:bg-slate-50">
                <td class="px-4 py-3">
                  <p class="font-semibold text-slate-700">{{ item.tipo_promocion_label }}</p>
                  <p class="text-xs text-slate-500">{{ format(item.executions) }} ejecuciones</p>
                </td>
                <td class="px-4 py-3 text-right">{{ money() }} {{ format(item.profit) }}</td>
                <td class="px-4 py-3 text-right">{{ format(item.margin_percent) }} %</td>
              </tr>
              <tr v-if="!rankings.promotion_types.length && !loading">
                <td colspan="3" class="px-4 py-6 text-center text-slate-400">
                  Sin datos por tipo de oferta.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div class="border-b px-4 py-3">
          <h3 class="text-sm font-bold text-slate-700">Clientes que más toman ofertas</h3>
          <p class="text-xs text-slate-500">Cuánto ha tomado cada cliente dentro del filtro actual.</p>
        </div>
        <div class="overflow-x-auto">
          <table class="table-auto w-full">
            <thead class="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th class="text-left px-4 py-3">Cliente</th>
                <th class="text-right px-4 py-3">Ventas</th>
                <th class="text-right px-4 py-3">Gratis</th>
              </tr>
            </thead>
            <tbody class="text-sm text-slate-600">
              <tr v-for="item in rankings.customers" :key="item.idcliente + item.nombre_cliente" class="odd:bg-white even:bg-slate-50">
                <td class="px-4 py-3 min-w-[220px] font-semibold text-slate-700">{{ item.nombre_cliente }}</td>
                <td class="px-4 py-3 text-right">{{ format(item.impacted_sales) }}</td>
                <td class="px-4 py-3 text-right">{{ format(item.bonus_quantity) }}</td>
              </tr>
              <tr v-if="!rankings.customers.length && !loading">
                <td colspan="3" class="px-4 py-6 text-center text-slate-400">
                  Sin clientes con ofertas en este período.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <section class="rounded-xl border bg-white shadow-sm overflow-hidden">
      <div class="border-b px-4 py-3 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h3 class="text-sm font-bold text-slate-700">Productos con mejor salida en ofertas</h3>
          <p class="text-xs text-slate-500">Cantidad vendida, gratis entregado y ganancia por producto.</p>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="table-auto w-full">
          <thead class="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th class="text-left px-4 py-3">Producto</th>
              <th class="text-right px-4 py-3">Vendida</th>
              <th class="text-right px-4 py-3">Gratis</th>
              <th class="text-right px-4 py-3">Ganancia</th>
            </tr>
          </thead>
          <tbody class="text-sm text-slate-600">
            <tr v-for="item in rankings.products" :key="item.idproducto" class="odd:bg-white even:bg-slate-50">
              <td class="px-4 py-3 min-w-[220px] font-semibold text-slate-700">{{ item.nombre_producto }}</td>
              <td class="px-4 py-3 text-right">{{ format(item.sold_quantity) }}</td>
              <td class="px-4 py-3 text-right">{{ format(item.bonus_quantity) }}</td>
              <td class="px-4 py-3 text-right">{{ money() }} {{ format(item.profit) }}</td>
            </tr>
            <tr v-if="!rankings.products.length && !loading">
              <td colspan="4" class="px-4 py-6 text-center text-slate-400">
                No hay productos con ofertas en el período seleccionado.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="rounded-xl border bg-white shadow-sm overflow-hidden">
      <div class="border-b px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 class="text-sm font-bold text-slate-700">Detalle de ofertas aplicadas</h3>
          <p class="text-xs text-slate-500">Base para auditoría y análisis detallado por venta.</p>
        </div>
        <select
          v-model="limitRegister"
          class="bg-white border py-2 text-sm px-3 text-gray-600 cursor-pointer rounded-lg outline-none min-w-[140px] font-semibold w-full md:w-auto"
          @change="resetValuesAndGetData"
        >
          <option value="10">10 Registros</option>
          <option value="25">25 Registros</option>
          <option value="50">50 Registros</option>
          <option value="100">100 Registros</option>
        </select>
      </div>
      <div class="w-full resize-x resize-generate" ref="widthNav" @resize="handleResize">
        <div class="overflow-x-auto">
          <table class="table-auto w-full" id="promotionOffersTable">
            <TableHead :headers="header" />
            <LoadingTables v-if="loading" :columns="10" />
            <tbody class="text-gray-500 text-left border-t-[1px]" v-else>
              <tr
                v-for="register in registers"
                :key="register.iddetalle_venta"
                class="hover:bg-blue-500 transition-colors hover:text-white capitalize font-semibold odd:bg-white even:bg-slate-50"
                @click="selectRow($event)"
              >
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ formatDateTime(register.fecha_de_emision) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap uppercase">
                  {{ register.serie_comprobante }} {{ register.numero_comprobante }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px]">
                  {{ register.nombre_cliente }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px] uppercase">
                  {{ register.nombre_producto }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 min-w-[220px]">
                  {{ register.promociones }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.cantidad_vendida }} / {{ register.cantidad_bonificada }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ register.unidad_de_medida }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ money() }} {{ format(register.total_final) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap">
                  {{ money() }} {{ format(register.ganancia) }}
                </td>
                <td class="border-slate-200 text-sm px-6 py-2 whitespace-nowrap text-right">
                  {{ format(register.margen_porcentaje) }} %
                </td>
              </tr>
              <tr v-if="!registers.length && !loading">
                <td colspan="10" class="px-6 py-8 text-center text-slate-400">
                  No encontramos ofertas con los filtros actuales.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <TablePagination
        :currentPage="currentPage"
        :total="totalPage"
        :next="next"
        :prev="prev"
      />
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref, watchEffect } from "vue";
import axios from "@/api";
import { errorActions, warningActions } from "@/alerts";
import LoadingTables from "@/components/Loadings/LoadingTables";
import TableHead from "@/components/TablesComponents/TableHead";
import TablePagination from "@/components/TablesComponents/TablePagination.vue";
import SearchCustomer from "@/components/Reports/Sales/General/SearchCustomer.vue";
import SearchProducts from "@/components/Reports/ComparePriceBetweenProviders/SearchProducts.vue";
import { observeElement } from "@/observer";
import { idLocalStore } from "@/publicjs/localStorage";
import { firstDaytAndLastDay } from "@/publicjs/getFirstAndLastDayOfMount";
import { money, format } from "@/publicjs/money";
import { formatDateTime } from "@/publicjs/formatDate";
import { selectRow } from "@/publicjs/selectRowTable";

export default {
  name: "MainPromotionOffersReport",
  components: {
    LoadingTables,
    TableHead,
    TablePagination,
    SearchCustomer,
    SearchProducts,
  },
  setup() {
    const loading = ref(true);
    const idStore = ref(0);
    const currentPage = ref(1);
    const totalPage = ref(1);
    const limitRegister = ref(10);
    const registers = ref([]);
    const rankings = ref({
      promotions: [],
      promotion_types: [],
      customers: [],
      products: [],
    });
    const insights = ref({
      most_executed_promotion: null,
      most_delivered_promotion: null,
      best_promotion_type: null,
    });
    const summary = ref({
      impacted_details: 0,
      impacted_sales: 0,
      sold_quantity: 0,
      bonus_quantity: 0,
      delivered_quantity: 0,
      revenue: 0,
      cost: 0,
      profit: 0,
      margin_percent: 0,
    });

    const header = [
      [
        { title: "fecha" },
        { title: "venta" },
        { title: "cliente" },
        { title: "producto" },
        { title: "ofertas" },
        { title: "vendida / gratis" },
        { title: "U.M" },
        { title: "ingreso" },
        { title: "ganancia" },
        { title: "margen" },
      ],
    ];

    const filters = ref({
      dateStart: null,
      dateEnd: null,
      promotionType: "ALL",
      search: "",
      idProduct: 0,
      productName: "",
      idCustomer: 0,
      customerName: "",
    });

    const summaryCards = computed(() => {
      const mainPromotion = insights.value.most_executed_promotion;
      const mainType = insights.value.best_promotion_type;

      return [
        {
          title: "Ventas impactadas",
          value: format(summary.value.impacted_sales),
          description: `${format(summary.value.impacted_details)} líneas con oferta aplicada`,
        },
        {
          title: "Cantidad vendida",
          value: format(summary.value.sold_quantity),
          description: `${format(summary.value.bonus_quantity)} gratis entregado`,
        },
        {
          title: "Ingreso con oferta",
          value: `${money()} ${format(summary.value.revenue)}`,
          description: `Ganancia ${money()} ${format(summary.value.profit)}`,
        },
        {
          title: "Margen obtenido",
          value: `${format(summary.value.margin_percent)} %`,
          description: mainType
            ? `Mejor tipo: ${mainType.tipo_promocion_label}`
            : "Sin tipo dominante en este filtro",
        },
        {
          title: "Oferta más ejecutada",
          value: mainPromotion ? format(mainPromotion.executions) : "0",
          description: mainPromotion
            ? mainPromotion.nombre_promocion
            : "No hay promociones aplicadas",
        },
        {
          title: "Salida total entregada",
          value: format(summary.value.delivered_quantity),
          description: "Vendida más producto bonificado",
        },
      ];
    });

    const getDataProduct = (data) => {
      filters.value.idProduct = data.id;
      filters.value.productName = data.productName;
    };

    const getDataCustomer = (data) => {
      filters.value.idCustomer = data.id;
      filters.value.customerName = data.fullName;
    };

    const removeProductFilter = () => {
      filters.value.idProduct = 0;
      filters.value.productName = "";
    };

    const removeCustomerFilter = () => {
      filters.value.idCustomer = 0;
      filters.value.customerName = "";
    };

    const getData = async () => {
      loading.value = true;

      const response = await axios
        .get(`reporte-de-ofertas/${idStore.value}`, {
          params: {
            date_start: filters.value.dateStart,
            date_end: filters.value.dateEnd,
            id_product: filters.value.idProduct,
            id_customer: filters.value.idCustomer,
            promotion_type: filters.value.promotionType,
            search: filters.value.search,
            page: currentPage.value,
            per_page: limitRegister.value,
          },
        })
        .catch((error) => errorActions(error));

      if (!response) {
        loading.value = false;
        return;
      }

      const data = response.data;
      summary.value = data.summary || summary.value;
      rankings.value = data.rankings || rankings.value;
      insights.value = data.insights || insights.value;
      registers.value = data.details?.data || [];
      currentPage.value = data.details?.current_page || 1;
      totalPage.value = data.details?.last_page || 1;

      if (!registers.value.length && !summary.value.impacted_details) {
        warningActions("No encontramos ofertas aplicadas con ese filtro");
      }

      loading.value = false;
    };

    const generateReport = async () => {
      currentPage.value = 1;
      await getData();
    };

    const resetValuesAndGetData = async () => {
      currentPage.value = 1;
      await getData();
    };

    const clearFilters = async () => {
      const dates = await firstDaytAndLastDay();
      filters.value = {
        dateStart: dates.firstDay,
        dateEnd: dates.lastDay,
        promotionType: "ALL",
        search: "",
        idProduct: 0,
        productName: "",
        idCustomer: 0,
        customerName: "",
      };
      await generateReport();
    };

    const next = async () => {
      if (currentPage.value < totalPage.value) {
        currentPage.value++;
        await getData();
      }
    };

    const prev = async () => {
      if (currentPage.value > 1) {
        currentPage.value--;
        await getData();
      }
    };

    const widthNav = ref(null);
    const handleResize = () => {
      const nav = document.querySelector("nav")?.offsetWidth || 0;
      watchEffect(() => {
        if (widthNav.value) {
          widthNav.value.style.width = `calc(100vw - ${nav + 42}px)`;
        }
      });
    };

    onMounted(async () => {
      observeElement("#contentPromotionOffersReport");
      observeElement("#promotionOffersTable");
      handleResize();
      window.addEventListener("resize", handleResize);
      idStore.value = await idLocalStore();
      const dates = await firstDaytAndLastDay();
      filters.value.dateStart = dates.firstDay;
      filters.value.dateEnd = dates.lastDay;
      await getData();
    });

    return {
      loading,
      filters,
      header,
      rankings,
      insights,
      summary,
      registers,
      currentPage,
      totalPage,
      limitRegister,
      generateReport,
      resetValuesAndGetData,
      clearFilters,
      next,
      prev,
      getDataProduct,
      getDataCustomer,
      removeProductFilter,
      removeCustomerFilter,
      money,
      format,
      formatDateTime,
      selectRow,
      summaryCards,
      widthNav,
      handleResize,
    };
  },
};
</script>

<style scoped>
.active button svg {
  rotate: 90deg;
}
</style>